import React from 'react';
import { Button } from '../../ui/Button';
import { CheckIcon, ShieldCheckIcon, ClockIcon, CreditCardIcon, HeartIcon, BadgeCheckIcon, ArrowRightIcon, PhoneIcon } from 'lucide-react';
export const AIPricing = () => {
  const openCalendly = () => {
    window.open('https://calendly.com/zerototendigital/exploration-feature-design', '_blank');
  };
  const plans = [{
    name: 'Starter',
    price: '$4,500',
    period: 'per month',
    hourlyRate: '$110/hour',
    hours: '10-12 hours/week',
    description: 'For teams exploring AI',
    aiNote: 'AI opportunity audit and prioritized roadmap',
    features: ['AI opportunity assessment', 'Use case prioritization', 'Build vs buy guidance', 'Weekly check-ins', 'Email support'],
    popular: false,
    buttonVariant: 'secondary' as const
  }, {
    name: 'Growth',
    price: '$8,500',
    period: 'per month',
    hourlyRate: '$98/hour',
    hours: '18-22 hours/week',
    description: 'For companies shipping AI',
    aiNote: 'POC development, evaluation frameworks, and production deployment support',
    features: ['Full AI roadmap ownership', 'POC design & delivery', 'LLM evaluation frameworks', 'Engineering coordination', '2x weekly check-ins', 'Priority support'],
    popular: true,
    buttonVariant: 'primary' as const
  }, {
    name: 'Scale',
    price: '$15,999',
    period: 'per month',
    hourlyRate: '$100/hour',
    hours: '35-40 hours/week',
    description: 'For AI-first transformation',
    aiNote: 'Full lifecycle AI leadership across product, ops, and infrastructure',
    features: ['Dedicated Head of AI', 'Cross-functional AI leadership', 'Governance & risk framework', 'Team hiring & enablement', 'Executive reporting', 'Daily availability', '24/7 priority support'],
    popular: false,
    buttonVariant: 'secondary' as const
  }, {
    name: 'Custom',
    price: 'Custom',
    period: 'pricing',
    hourlyRate: 'Tailored',
    hours: 'Flexible hours',
    description: 'For enterprise & regulated industries',
    aiNote: 'Tailored AI leadership with compliance, security, and custom integrations',
    features: ['Fully customized engagement', 'Dedicated AI team', 'Executive strategy sessions', 'Enterprise integrations', 'Compliance & security review', 'Strategic consulting', 'Dedicated account manager'],
    popular: false,
    buttonVariant: 'outline' as const
  }];
  return <section id="ai-pricing" className="py-20 bg-white relative overflow-hidden" data-id="element-193">
      <div className="absolute top-0 right-0 w-72 h-72 bg-purple-50 rounded-full opacity-50 -mr-36 -mt-36" data-id="element-194"></div>
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-purple-50 rounded-full opacity-40 -ml-40 -mb-40" data-id="element-195"></div>
      <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-196">
        <div className="text-center mb-12" data-id="element-197">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-black" data-id="element-198">
            Simple, Transparent Pricing
          </h2>
          <p className="text-xl text-text-secondary max-w-2xl mx-auto" data-id="element-199">
            Choose the AI leadership plan that fits your stage.
          </p>
        </div>
        <div className="flex justify-center mb-10" data-id="element-200">
          <div className="bg-purple-50 rounded-full px-5 py-2 inline-flex items-center" data-id="element-201">
            <ShieldCheckIcon className="h-5 w-5 text-primary mr-2" data-id="element-202" />
            <span className="text-sm font-medium" data-id="element-203">
              AI lead in 48h • POC support included • Interview optional
            </span>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto" data-id="element-204">
          {plans.map((plan, index) => <div key={index} className={`bg-white rounded-xl border ${plan.popular ? 'border-primary shadow-lg ring-2 ring-primary ring-opacity-20' : 'border-gray-200 shadow-sm hover:shadow-md'} transition-all duration-300 flex flex-col h-full relative`} data-id="element-205">
              {plan.popular && <div className="absolute -top-4 -right-4" data-id="element-206">
                  <div className="bg-primary text-white text-xs font-bold px-3 py-1 rounded-full shadow-md flex items-center" data-id="element-207">
                    <BadgeCheckIcon className="h-3 w-3 mr-1" data-id="element-208" />
                    MOST POPULAR
                  </div>
                </div>}
              <div className="p-6 border-b border-gray-100" data-id="element-209">
                <h3 className="text-xl font-bold text-black mb-1" data-id="element-210">
                  {plan.name}
                </h3>
                <p className="text-sm text-gray-500 mb-2" data-id="element-211">{plan.description}</p>
                <p className="text-xs text-green-600 mb-4" data-id="element-212">{plan.aiNote}</p>
                <div className="flex items-baseline mb-1" data-id="element-213">
                  <span className="text-3xl font-bold text-black" data-id="element-214">
                    {plan.price}
                  </span>
                  <span className="text-gray-500 ml-1 text-sm" data-id="element-215">
                    {plan.period}
                  </span>
                </div>
                <div className="flex flex-col space-y-2 mb-4" data-id="element-216">
                  <div className="flex items-center text-primary-dark font-medium" data-id="element-217">
                    <ClockIcon className="w-4 h-4 mr-2" data-id="element-218" />
                    <span className="text-lg" data-id="element-219">{plan.hours}</span>
                  </div>
                  <div className="text-sm text-gray-500" data-id="element-220">
                    Effective rate: {plan.hourlyRate}
                  </div>
                </div>
              </div>
              <div className="p-6 flex-grow" data-id="element-221">
                <ul className="space-y-3 mb-6" data-id="element-222">
                  {plan.features.map((feature, idx) => <li key={idx} className="flex items-start" data-id="element-223">
                      <div className="flex-shrink-0 mt-1" data-id="element-224">
                        <div className="w-4 h-4 rounded-full bg-primary bg-opacity-10 flex items-center justify-center" data-id="element-225">
                          <CheckIcon className="w-3 h-3 text-primary" data-id="element-226" />
                        </div>
                      </div>
                      <span className="text-gray-700 ml-3 text-sm" data-id="element-227">
                        {feature}
                      </span>
                    </li>)}
                </ul>
              </div>
              <div className="p-6 pt-0 mt-auto" data-id="element-228">
                <Button variant={plan.buttonVariant} className={`w-full ${plan.popular ? 'bg-primary text-white hover:bg-primary-dark' : ''}`} onClick={openCalendly} data-id="element-229">
                  Speak with an AI Lead
                  {plan.name === 'Custom' ? <PhoneIcon className="ml-2 h-4 w-4" data-id="element-230" /> : <ArrowRightIcon className="ml-2 h-4 w-4" data-id="element-231" />}
                </Button>
              </div>
            </div>)}
        </div>
        <div className="mt-24 max-w-5xl mx-auto" data-id="element-232">
          <div className="text-center mb-12" data-id="element-233">
            <h3 className="text-2xl md:text-3xl font-bold text-black" data-id="element-234">
              Our Guarantees
            </h3>
            <p className="text-text-secondary mt-3" data-id="element-235">
              We stand behind our service with these promises
            </p>
            <div className="inline-block bg-green-50 text-green-600 px-4 py-2 rounded-full text-sm font-medium mt-4" data-id="element-236">
              AI expertise guaranteed — if your AI lead isn't a fit, we'll swap
              or refund 75% within 14 days.
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-237">
            <div className="bg-purple-50 rounded-xl p-6 flex items-start" data-id="element-238">
              <div className="bg-white p-3 rounded-full mr-4 shadow-sm" data-id="element-239">
                <ShieldCheckIcon className="w-6 h-6 text-primary" data-id="element-240" />
              </div>
              <div data-id="element-241">
                <h4 className="font-bold text-lg mb-2" data-id="element-242">
                  Satisfaction Guarantee
                </h4>
                <p className="text-text-secondary text-sm" data-id="element-243">
                  If you're not satisfied within the first 14 days, we'll refund
                  75% of your subscription — no questions asked.
                </p>
              </div>
            </div>
            <div className="bg-purple-50 rounded-xl p-6 flex items-start" data-id="element-244">
              <div className="bg-white p-3 rounded-full mr-4 shadow-sm" data-id="element-245">
                <ClockIcon className="w-6 h-6 text-primary" data-id="element-246" />
              </div>
              <div data-id="element-247">
                <h4 className="font-bold text-lg mb-2" data-id="element-248">Quick Onboarding</h4>
                <p className="text-text-secondary text-sm" data-id="element-249">
                  Get matched with your AI lead and start working within 48
                  hours. No lengthy hiring processes.
                </p>
              </div>
            </div>
            <div className="bg-purple-50 rounded-xl p-6 flex items-start" data-id="element-250">
              <div className="bg-white p-3 rounded-full mr-4 shadow-sm" data-id="element-251">
                <CreditCardIcon className="w-6 h-6 text-primary" data-id="element-252" />
              </div>
              <div data-id="element-253">
                <h4 className="font-bold text-lg mb-2" data-id="element-254">Flexible Billing</h4>
                <p className="text-text-secondary text-sm" data-id="element-255">
                  Change or cancel your plan anytime. Scale up during critical
                  phases and scale down when appropriate.
                </p>
              </div>
            </div>
            <div className="bg-purple-50 rounded-xl p-6 flex items-start" data-id="element-256">
              <div className="bg-white p-3 rounded-full mr-4 shadow-sm" data-id="element-257">
                <HeartIcon className="w-6 h-6 text-primary" data-id="element-258" />
              </div>
              <div data-id="element-259">
                <h4 className="font-bold text-lg mb-2" data-id="element-260">Quality Commitment</h4>
                <p className="text-text-secondary text-sm" data-id="element-261">
                  All our AI leaders have shipped production AI at top tech
                  companies. We only work with proven operators.
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-24 mb-8 max-w-4xl mx-auto bg-gradient-to-r from-primary to-primary-light rounded-2xl overflow-hidden shadow-lg" data-id="element-262">
          <div className="px-8 py-12 text-center" data-id="element-263">
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4" data-id="element-264">
              Ready to lead with AI?
            </h3>
            <p className="text-white text-opacity-90 mb-8 max-w-2xl mx-auto" data-id="element-265">
              Get a senior AI leader embedded in your team within 48 hours.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-266">
              <Button variant="secondary" className="bg-white text-primary hover:bg-gray-100 border-0" onClick={openCalendly} data-id="element-267">
                Speak with an AI Lead
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
};